import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend,
  PieChart, Pie, Cell
} from 'recharts';
import { formatCurrency } from '@/lib/currency';
import { SidebarNav } from '@/pages/AccountsPage';
import {
  LogOut, Menu, X, Download, FileSpreadsheet, FileText,
  TrendingUp, TrendingDown, Repeat, PiggyBank, FileBarChart, FileJson
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#6366F1', '#14B8A6'];

const ReportsPage = () => {
  const { user, logout, isAdmin, currency, locale } = useAuth();
  const navigate = useNavigate();
  const fmt = (v) => formatCurrency(v, currency, locale);

  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cashFlow, setCashFlow] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [recurring, setRecurring] = useState(null);
  const [savings, setSavings] = useState(null);
  const [period, setPeriod] = useState('current_month');
  const [months, setMonths] = useState(6);
  const [downloading, setDownloading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [cf, cb, rc, sv] = await Promise.all([
        fetch(`${API_URL}/api/reports/cash-flow?months=${months}`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/reports/category-breakdown?period=${period}`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/reports/recurring-summary`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/reports/savings-progress`, { headers }).then(r => r.ok ? r.json() : null),
      ]);
      setCashFlow(cf);
      setBreakdown(cb);
      setRecurring(rc);
      setSavings(sv);
    } finally {
      setLoading(false);
    }
  }, [months, period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleExport = async (format) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/export/transactions?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const blob = format === 'json'
        ? new Blob([JSON.stringify(await res.json(), null, 2)], { type: 'application/json' })
        : await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'xlsx' ? 'xlsx' : (format === 'excel' ? 'xlsx' : format);
      a.download = `wsfinances_transactions.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  // Build MoM comparison
  const lastTwo = cashFlow?.series?.slice(-2) || [];
  const momIncome = lastTwo.length === 2 && lastTwo[0].income > 0
    ? ((lastTwo[1].income - lastTwo[0].income) / lastTwo[0].income * 100).toFixed(1)
    : null;
  const momExpense = lastTwo.length === 2 && lastTwo[0].expense > 0
    ? ((lastTwo[1].expense - lastTwo[0].expense) / lastTwo[0].expense * 100).toFixed(1)
    : null;

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-label="Menú">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 p-2 -mr-2" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <SidebarNav active="reports" isAdmin={isAdmin} onClick={() => setMobileMenuOpen(false)} />
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>
        <SidebarNav active="reports" isAdmin={isAdmin} />
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-zinc-300 p-2 -ml-2" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú">
              <Menu className="h-6 w-6" />
            </button>
            <div className="min-w-0">
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Reportes</h2>
              <p className="text-zinc-400 text-sm">Cash flow, categorías, recurrentes y exportación</p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-violet-600 hover:bg-violet-700" disabled={downloading} data-testid="export-menu-btn">
                <Download className="h-4 w-4 mr-2" /> Exportar todo
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
              <DropdownMenuLabel className="text-zinc-400">Formato</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf"><FileText className="h-4 w-4 mr-2" /> PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-2" /> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv"><FileBarChart className="h-4 w-4 mr-2" /> CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')} data-testid="export-json"><FileJson className="h-4 w-4 mr-2" /> JSON</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Tabs defaultValue="cashflow" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto">
            <TabsTrigger value="cashflow" data-testid="tab-cashflow">Cash flow</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categorías</TabsTrigger>
            <TabsTrigger value="recurring" data-testid="tab-recurring">Recurrentes</TabsTrigger>
            <TabsTrigger value="savings" data-testid="tab-savings">Ahorros</TabsTrigger>
          </TabsList>

          {/* CASH FLOW */}
          <TabsContent value="cashflow" className="mt-4 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-sm text-zinc-400">Periodo:</span>
              <Select value={String(months)} onValueChange={(v) => setMonths(parseInt(v, 10))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 w-48" data-testid="months-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Últimos 3 meses</SelectItem>
                  <SelectItem value="6">Últimos 6 meses</SelectItem>
                  <SelectItem value="12">Últimos 12 meses</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-400">Ingresos vs mes anterior</p>
                    <TrendingUp className="h-4 w-4 text-emerald-400" />
                  </div>
                  <p className="text-2xl font-bold text-white mt-1">
                    {momIncome !== null ? `${momIncome > 0 ? '+' : ''}${momIncome}%` : '—'}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-400">Gastos vs mes anterior</p>
                    <TrendingDown className="h-4 w-4 text-red-400" />
                  </div>
                  <p className="text-2xl font-bold text-white mt-1">
                    {momExpense !== null ? `${momExpense > 0 ? '+' : ''}${momExpense}%` : '—'}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-zinc-900/40 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Flujo de caja mensual</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72 sm:h-96 min-h-[280px]">
                  {cashFlow?.series?.length ? (
                    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                      <LineChart data={cashFlow.series}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                        <YAxis stroke="#71717a" fontSize={12} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                          formatter={(v) => fmt(v)}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="income" name="Ingresos" stroke="#10B981" strokeWidth={2} />
                        <Line type="monotone" dataKey="expense" name="Gastos" stroke="#EF4444" strokeWidth={2} />
                        <Line type="monotone" dataKey="net" name="Neto" stroke="#8B5CF6" strokeWidth={2} strokeDasharray="4 4" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-zinc-500 text-sm">Cargando…</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CATEGORIES */}
          <TabsContent value="categories" className="mt-4 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-sm text-zinc-400">Periodo:</span>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 w-48" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current_month">Mes actual</SelectItem>
                  <SelectItem value="last_month">Mes anterior</SelectItem>
                  <SelectItem value="ytd">Año actual (YTD)</SelectItem>
                </SelectContent>
              </Select>
              {breakdown && (
                <span className="text-sm text-zinc-500">
                  {breakdown.start_date} → {breakdown.end_date}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-zinc-900/40 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Distribución</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72 min-h-[280px]">
                    {breakdown?.breakdown?.length ? (
                      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                        <PieChart>
                          <Pie
                            data={breakdown.breakdown}
                            dataKey="amount"
                            nameKey="category"
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={90}
                            paddingAngle={2}
                            label={({ category, percentage }) => `${category} ${percentage}%`}
                            labelLine={false}
                          >
                            {breakdown.breakdown.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                            formatter={(v) => fmt(v)}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">Sin datos</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/40 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg">
                    Top categorías {breakdown && <span className="text-sm font-normal text-zinc-400">· total {fmt(breakdown.total)}</span>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {breakdown?.breakdown?.length ? (
                    <ul className="space-y-3">
                      {breakdown.breakdown.map((c, idx) => (
                        <li key={c.category}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-zinc-200 truncate">{idx + 1}. {c.category} <span className="text-zinc-500 text-xs">({c.count})</span></span>
                            <span className="text-zinc-300 font-medium shrink-0">{fmt(c.amount)}</span>
                          </div>
                          <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${c.percentage}%`, backgroundColor: COLORS[idx % COLORS.length] }} />
                          </div>
                          <p className="text-xs text-zinc-500 mt-1">{c.percentage}%</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-zinc-500 text-sm py-6 text-center">Sin gastos en este periodo</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* RECURRING */}
          <TabsContent value="recurring" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-zinc-400">Total mensual</p>
                    <Repeat className="h-4 w-4 text-violet-400" />
                  </div>
                  <p className="text-2xl font-bold text-red-400">{fmt(recurring?.total_monthly || 0)}</p>
                  <p className="text-xs text-zinc-500 mt-1">{recurring?.count || 0} recurrentes activos</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-5">
                  <p className="text-xs text-zinc-400 mb-1">Estimado anual</p>
                  <p className="text-2xl font-bold text-amber-400">{fmt(recurring?.annual_estimate || 0)}</p>
                  <p className="text-xs text-zinc-500 mt-1">12 × mensual</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-5">
                  <p className="text-xs text-zinc-400 mb-1">Mayor categoría</p>
                  <p className="text-lg font-bold text-white truncate">{recurring?.by_category?.[0]?.category || '—'}</p>
                  <p className="text-xs text-zinc-500 mt-1">{recurring?.by_category?.[0] ? fmt(recurring.by_category[0].amount) + ' / mes' : 'sin datos'}</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-zinc-900/40 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Por categoría</CardTitle>
                </CardHeader>
                <CardContent>
                  {recurring?.by_category?.length ? (
                    <div className="h-64 min-h-[240px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                        <BarChart data={recurring.by_category} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis type="number" stroke="#71717a" fontSize={12} />
                          <YAxis dataKey="category" type="category" stroke="#71717a" fontSize={12} width={100} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                            formatter={(v) => fmt(v)}
                          />
                          <Bar dataKey="amount" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm py-8 text-center">Sin recurrentes activos</p>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/40 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Calendario mensual</CardTitle>
                </CardHeader>
                <CardContent>
                  {recurring?.items?.length ? (
                    <ul className="space-y-2">
                      {recurring.items.map((r) => (
                        <li key={r.id} className="flex justify-between items-center py-2 border-b border-zinc-800 last:border-0">
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-medium truncate">{r.description}</p>
                            <p className="text-xs text-zinc-500">{r.category} · día {r.day_of_month}</p>
                          </div>
                          <span className="text-red-400 font-semibold">{fmt(r.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-zinc-500 text-sm py-8 text-center">Sin recurrentes activos</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SAVINGS */}
          <TabsContent value="savings" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-5">
                  <p className="text-xs text-zinc-400 mb-1">Total ahorrado</p>
                  <p className="text-2xl font-bold text-emerald-400">{fmt(savings?.total_current || 0)}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-5">
                  <p className="text-xs text-zinc-400 mb-1">Objetivo total</p>
                  <p className="text-2xl font-bold text-white">{fmt(savings?.total_target || 0)}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-zinc-400">Progreso global</p>
                    <PiggyBank className="h-4 w-4 text-emerald-400" />
                  </div>
                  <p className="text-2xl font-bold text-violet-300">{savings?.progress_percentage || 0}%</p>
                </CardContent>
              </Card>
            </div>

            {savings?.goals?.length ? (
              <Card className="bg-zinc-900/40 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Progreso por meta</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {savings.goals.map(g => (
                      <li key={g.id}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-white font-medium">{g.name} <span className="text-zinc-500 text-xs ml-1">({g.type === 'emergency_fund' ? 'Emergencia' : 'Ahorro'})</span></span>
                          <span className="text-zinc-300">{fmt(g.current_amount)} / {fmt(g.target_amount)}</span>
                        </div>
                        <div className="w-full h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, g.progress)}%`, backgroundColor: g.color }} />
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">{g.progress}% completado</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-zinc-900/40 border-zinc-800">
                <CardContent className="p-8 text-center text-zinc-500">
                  Aún no tienes metas creadas.
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {loading && <p className="text-zinc-500 text-sm mt-4">Cargando…</p>}
      </main>
    </div>
  );
};

export default ReportsPage;
